import React, { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { collection, query, where, getDocs, getDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, CircularProgress, ThemeProvider } from '@mui/material';
import { db } from '../firebase';
import { ORDER_STATUSES, PAYMENT_STATUS_LABELS } from '../firebase/schema';
import { syncOrderPaymentIfInvoiceable, finalizeOrderPaymentOnInvoice } from '../utils/orderPayment';
import { formatDate } from '../utils/dateFormatter';
import { theme as muiTheme } from '../styles/MUITheme';
import Invoice from '../components/Invoice';
import html2pdf from 'html2pdf.js';
import dropboxService from '../utils/dropboxService';
import { fixOrphanedInvoiceableOrders } from '../utils/fixOrphanedOrders';
import DropboxConfig from '../components/DropboxConfig';
import { useFacility } from '../context/FacilityContext';
import { filterByFacility } from '../utils/facilities';

const PageContainer = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 20px;
  padding: 80px 20px 20px 20px;
  min-height: 100vh;
  background: var(--al-bg);
`;

const OrdersList = styled.div`
  background: var(--al-surface);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 16px;
  height: calc(100vh - 100px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const OrdersListHeader = styled.div`
  margin-bottom: 16px;
`;

const FixButton = styled.button`
  background: #FFA500;
  color: #222;
  border: none;
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  width: 100%;
  transition: all 0.2s;

  &:hover {
    background: #FF8C00;
  }

  &:disabled {
    background: #666;
    cursor: not-allowed;
  }
`;

const DropboxButton = styled.button`
  background: ${props => props.connected ? 'rgba(var(--al-primary-rgb), 0.2)' : '#0061FF'};
  color: ${props => props.connected ? 'var(--al-primary)' : 'white'};
  border: 1px solid ${props => props.connected ? 'rgba(var(--al-primary-rgb), 0.3)' : 'transparent'};
  border-radius: 4px;
  padding: 8px 12px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  width: 100%;
  transition: all 0.2s;
  margin-top: 8px;

  &:hover {
    background: ${props => props.connected ? 'rgba(var(--al-primary-rgb), 0.3)' : '#0052D9'};
  }
`;


const OrderItem = styled.div`
  padding: 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(var(--al-primary-rgb), 0.05);
  }
  
  ${props => props.selected && `
    background-color: rgba(var(--al-primary-rgb), 0.1);
  `}
`;

const OrderTitle = styled.div`
  font-size: 0.9em;
  font-weight: 600;
  color: white;
  margin-bottom: 4px;
`;

const OrderDetails = styled.div`
  font-size: 0.8em;
  color: rgba(255, 255, 255, 0.6);
`;

const InvoiceContainer = styled.div`
  background: var(--al-surface);
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 24px;
  height: calc(100vh - 100px);
  overflow-y: auto;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 24px;
  justify-content: flex-end;
`;

const SendButton = styled.button`
  background: var(--al-primary);
  color: #222;
  border: none;
  border-radius: 4px;
  padding: 12px 24px;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);

  &:hover {
    background: #7FFF7F;
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
`;

const ArchiveButton = styled(SendButton)`
  background: #666;
  color: white;

  &:hover {
    background: #777;
  }
`;

const NoInvoiceSelected = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  color: rgba(255, 255, 255, 0.6);
  font-size: 1.1em;
`;

const Invoicing = () => {
  const { facilityId } = useFacility();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendEmailConfirmOpen, setSendEmailConfirmOpen] = useState(false);
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [fixingOrders, setFixingOrders] = useState(false);
  const [dropboxConfigOpen, setDropboxConfigOpen] = useState(false);
  const [dropboxConnected, setDropboxConnected] = useState(false);
  const invoiceRef = useRef(null);

  // Check Dropbox connection status on mount
  useEffect(() => {
    const checkDropboxStatus = async () => {
      try {
        const status = await dropboxService.getConnectionStatus();
        setDropboxConnected(status.connected);
      } catch (error) {
        console.warn('Could not check Dropbox status:', error);
        setDropboxConnected(false);
      }
    };
    checkDropboxStatus();
  }, []);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const q = query(
          collection(db, 'orders'),
          where('status', '==', ORDER_STATUSES.READY_TO_INVOICE)
        );
        
        const querySnapshot = await getDocs(q);
        const ordersData = [];
        
        for (const docSnapshot of querySnapshot.docs) {
          const orderData = {
            id: docSnapshot.id,
            ...docSnapshot.data()
          };
          
          // Fetch customer data to get email
          if (orderData.customerId) {
            const customerDoc = await getDoc(doc(db, 'customers', orderData.customerId));
            if (customerDoc.exists()) {
              orderData.contactEmail = customerDoc.data().email;
            }
          }
          
          ordersData.push(orderData);
        }

        await Promise.all(ordersData.map((o) => syncOrderPaymentIfInvoiceable(db, o.id)));

        const refreshedSnap = await getDocs(q);
        const refreshed = [];
        for (const docSnapshot of refreshedSnap.docs) {
          const orderData = { id: docSnapshot.id, ...docSnapshot.data() };
          if (orderData.customerId) {
            const customerDoc = await getDoc(doc(db, 'customers', orderData.customerId));
            if (customerDoc.exists()) {
              orderData.contactEmail = customerDoc.data().email;
            }
          }
          refreshed.push(orderData);
        }

        const facilityOrders = filterByFacility(refreshed, facilityId);
        
        console.log('Fetched orders:', facilityOrders);
        setOrders(facilityOrders);
        
        if (facilityOrders.length > 0 && !selectedOrder) {
          await handleOrderSelect(facilityOrders[0]);
        } else if (selectedOrder && !facilityOrders.some((o) => o.id === selectedOrder.id)) {
          setSelectedOrder(null);
        }
      } catch (error) {
        console.error('Error fetching orders:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [facilityId]);

  const handleOrderSelect = async (order) => {
    try {
      // Fetch customer data if not already present
      if (order.customerId && !order.contactEmail) {
        const customerDoc = await getDoc(doc(db, 'customers', order.customerId));
        if (customerDoc.exists()) {
          order = {
            ...order,
            contactEmail: customerDoc.data().email
          };
        }
      }
      setSelectedOrder(order);
    } catch (error) {
      console.error('Error fetching customer data:', error);
      setSelectedOrder(order);
    }
  };

  const handleOrderCompleted = async (orderId) => {
    setArchiving(true);
    try {
      // Generate PDF from invoice
      if (!invoiceRef.current) {
        throw new Error('Invoice reference not available');
      }

      const element = invoiceRef.current;
      
      // Add class to hide edit buttons during PDF export
      element.classList.add('exporting-pdf');
      
      const opt = {
        margin: 0.5,
        filename: `Invoice_${selectedOrder.poNumber || selectedOrder.orderNumber}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, backgroundColor: '#ffffff' },
        jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
      };

      console.log('Generating PDF...');
      const pdfBlob = await html2pdf().set(opt).from(element).output('blob');
      console.log('PDF generated successfully');
      
      // Remove the export class after PDF is generated
      element.classList.remove('exporting-pdf');

      // Try to upload to Dropbox
      let dropboxUrl = null;
      try {
        // Check if Dropbox is configured (localStorage or hardcoded token)
        if (dropboxService.isConfigured()) {
          dropboxService.initialize(); // Will use localStorage or hardcoded token
          const clientName = selectedOrder.customerName || selectedOrder.client || 'Unknown';
          const filename = `${selectedOrder.poNumber || selectedOrder.orderNumber} - ${clientName}.pdf`;
          console.log('Uploading to Dropbox:', filename);
          const uploadResult = await dropboxService.uploadFile(pdfBlob, filename, '/87 INVOICES');
          dropboxUrl = uploadResult.url;
          console.log('Uploaded to Dropbox:', dropboxUrl);
        } else {
          console.warn('Dropbox not configured - skipping upload');
        }
      } catch (dropboxError) {
        console.error('Error uploading to Dropbox (continuing anyway):', dropboxError);
        // Continue with archiving even if Dropbox upload fails
      }

      // Update order status to completed
      const updateData = {
        status: ORDER_STATUSES.COMPLETED,
        archived: true,
        invoiced: true,
        invoicedAt: serverTimestamp(),
        completedAt: new Date(),
        updatedAt: new Date(),
        updatedBy: 'system' // or get current user
      };

      if (dropboxUrl) {
        updateData.invoiceDropboxUrl = dropboxUrl;
      }

      await finalizeOrderPaymentOnInvoice(db, orderId);
      await updateDoc(doc(db, 'orders', orderId), updateData);

      // Remove the completed order from the list
      const updatedOrders = orders.filter(order => order.id !== orderId);
      setOrders(updatedOrders);

      // Auto-select the next order (first in the remaining list)
      if (updatedOrders.length > 0) {
        await handleOrderSelect(updatedOrders[0]);
      } else {
        setSelectedOrder(null);
      }

      console.log('Order completed and archived successfully');
      alert(dropboxUrl ? 'Order archived and PDF uploaded to Dropbox!' : 'Order archived successfully!');
    } catch (error) {
      console.error('Error completing order:', error);
      alert('Failed to archive order: ' + error.message);
      throw error;
    } finally {
      setArchiving(false);
      setArchiveConfirmOpen(false);
    }
  };

  const handleInvoiceUpdate = async (updatedData) => {
    // Handle saving invoice updates to Firebase
    try {
      // Update logic here
      console.log('Updating invoice:', updatedData);
    } catch (error) {
      console.error('Error updating invoice:', error);
    }
  };

  const handleFixOrphanedOrders = async () => {
    setFixingOrders(true);
    try {
      const result = await fixOrphanedInvoiceableOrders();
      
      if (result.success) {
        alert(`Fixed ${result.updatedCount} orders!\n\nThese orders had all lines completed but were not marked as "Ready To Invoice".`);
        
        // Refresh the orders list
        const q = query(
          collection(db, 'orders'),
          where('status', '==', ORDER_STATUSES.READY_TO_INVOICE)
        );
        
        const querySnapshot = await getDocs(q);
        const ordersData = [];
        
        for (const docSnapshot of querySnapshot.docs) {
          const orderData = {
            id: docSnapshot.id,
            ...docSnapshot.data()
          };
          
          // Fetch customer data to get email
          if (orderData.customerId) {
            const customerDoc = await getDoc(doc(db, 'customers', orderData.customerId));
            if (customerDoc.exists()) {
              orderData.contactEmail = customerDoc.data().email;
            }
          }
          
          ordersData.push(orderData);
        }
        
        const facilityOrders = filterByFacility(ordersData, facilityId);
        setOrders(facilityOrders);
        
        // Auto-select the first order if orders exist and no order is currently selected
        if (facilityOrders.length > 0 && !selectedOrder) {
          await handleOrderSelect(facilityOrders[0]);
        }
      } else {
        alert(`Error fixing orders: ${result.error}`);
      }
    } catch (error) {
      console.error('Error fixing orphaned orders:', error);
      alert('Error fixing orders: ' + error.message);
    } finally {
      setFixingOrders(false);
    }
  };

  return (
    <PageContainer>
      <OrdersList>
        <OrdersListHeader>
          <FixButton 
            onClick={handleFixOrphanedOrders}
            disabled={fixingOrders || loading}
          >
            {fixingOrders ? '🔄 Scanning...' : '🔍 Find Missing Orders'}
          </FixButton>
          <DropboxButton 
            onClick={() => setDropboxConfigOpen(true)}
            connected={dropboxConnected}
          >
            {dropboxConnected ? '📦 Dropbox Connected' : '📦 Connect Dropbox'}
          </DropboxButton>
        </OrdersListHeader>
        
        {orders.map(order => (
          <OrderItem
            key={order.id}
            selected={selectedOrder?.id === order.id}
            onClick={() => handleOrderSelect(order)}
          >
            <OrderTitle>{order.customerName}</OrderTitle>
            <OrderDetails>
              Invoice: {order.poNumber}<br />
              Due: {formatDate(order.dueDate)}
              {order.payment?.status && (
                <>
                  <br />
                  Payment: {PAYMENT_STATUS_LABELS[order.payment.status] || order.payment.status}
                  {order.payment.amountDue != null && ` — $${Number(order.payment.amountDue).toFixed(2)}`}
                </>
              )}
            </OrderDetails>
          </OrderItem>
        ))}
      </OrdersList>

      <InvoiceContainer>
        {selectedOrder ? (
          <>
            <Invoice 
              key={selectedOrder?.id}
              order={selectedOrder}
              onUpdate={handleInvoiceUpdate}
              onOrderCompleted={handleOrderCompleted}
              invoiceRef={invoiceRef}
            />
            <ActionButtons>
              <SendButton onClick={() => setSendEmailConfirmOpen(true)}>
                📧 Send Invoice
              </SendButton>
              <ArchiveButton onClick={() => setArchiveConfirmOpen(true)}>
                📁 Complete & Archive Order
              </ArchiveButton>
            </ActionButtons>

            {/* Send Email Confirmation Dialog */}
            <ThemeProvider theme={muiTheme}>
            <Dialog
              open={sendEmailConfirmOpen}
              onClose={() => setSendEmailConfirmOpen(false)}
              maxWidth="sm"
              PaperProps={{
                style: {
                  backgroundColor: 'var(--al-surface)',
                  color: 'white',
                  minWidth: '400px'
                }
              }}
            >
              <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                Send Invoice Email
              </DialogTitle>
              <DialogContent sx={{ mt: 2 }}>
                <Typography>
                  Send invoice to {selectedOrder?.customerName} ({selectedOrder?.contactEmail})?
                </Typography>
                <Typography variant="body2" sx={{ mt: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
                  This will email a PDF copy of the invoice to the customer.
                </Typography>
              </DialogContent>
              <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', p: 2 }}>
                <Button
                  onClick={() => setSendEmailConfirmOpen(false)}
                  sx={{ color: 'rgba(255, 255, 255, 0.7)', '&:hover': { color: 'white' } }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    // TODO: Add email sending logic
                    setSendEmailConfirmOpen(false);
                  }}
                  sx={{
                    backgroundColor: 'var(--al-primary)',
                    color: '#222',
                    '&:hover': { backgroundColor: '#7FFF7F' }
                  }}
                >
                  Send Email
                </Button>
              </DialogActions>
            </Dialog>
            </ThemeProvider>

            {/* Archive Confirmation Dialog */}
            <ThemeProvider theme={muiTheme}>
            <Dialog
              open={archiveConfirmOpen}
              onClose={() => setArchiveConfirmOpen(false)}
              maxWidth="sm"
              PaperProps={{
                style: {
                  backgroundColor: 'var(--al-surface)',
                  color: 'white',
                  minWidth: '400px'
                }
              }}
            >
              <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
                Archive Order
              </DialogTitle>
              <DialogContent sx={{ mt: 2 }}>
                <Typography>
                  Complete and archive order {selectedOrder?.orderNumber}?
                </Typography>
                <Typography variant="body2" sx={{ mt: 2, color: 'rgba(255, 255, 255, 0.7)' }}>
                  This will:
                  <br />• Generate a PDF invoice
                  <br />• Upload it to Dropbox (if configured)
                  <br />• Move the order to completed status
                  <br />• Remove it from the active orders list
                </Typography>
              </DialogContent>
              <DialogActions sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.1)', p: 2 }}>
                <Button
                  onClick={() => setArchiveConfirmOpen(false)}
                  disabled={archiving}
                  sx={{ color: 'rgba(255, 255, 255, 0.7)', '&:hover': { color: 'white' } }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleOrderCompleted(selectedOrder.id)}
                  disabled={archiving}
                  sx={{
                    backgroundColor: 'var(--al-primary)',
                    color: '#222',
                    '&:hover': { backgroundColor: '#7FFF7F' },
                    '&:disabled': { backgroundColor: '#666' }
                  }}
                  startIcon={archiving ? <CircularProgress size={20} sx={{ color: '#222' }} /> : null}
                >
                  {archiving ? 'Archiving...' : 'Archive Order'}
                </Button>
              </DialogActions>
            </Dialog>
            </ThemeProvider>
          </>
        ) : (
          <NoInvoiceSelected>
            {loading ? 'Loading orders...' : 'No orders ready for invoicing'}
          </NoInvoiceSelected>
        )}
      </InvoiceContainer>

      {/* Dropbox Configuration Modal */}
      <DropboxConfig
        isOpen={dropboxConfigOpen}
        onClose={async () => {
          setDropboxConfigOpen(false);
          // Refresh connection status when modal closes
          try {
            const status = await dropboxService.getConnectionStatus();
            setDropboxConnected(status.connected);
          } catch (error) {
            console.warn('Could not refresh Dropbox status:', error);
          }
        }}
        onSave={async () => {
          // Refresh connection status
          try {
            const status = await dropboxService.getConnectionStatus();
            setDropboxConnected(status.connected);
          } catch (error) {
            console.warn('Could not refresh Dropbox status:', error);
          }
        }}
      />
    </PageContainer>
  );
};

export default Invoicing; 